import { Ed25519PrivateKey } from "@aptos-labs/ts-sdk";
import { EphemeralKeyPair } from "@aptos-labs/ts-sdk/keyless";

import { LOCAL_EPHEMERAL_KEY_PAIR_KEY } from "@/constants";

import { Logger } from "../logger";
import { decodeUint8Array, encodeUint8Array, PersistedUint8Array } from "./code";

interface PersistedEphemeralKeyPair {
  __type: "EphemeralKeyPair";
  data: PersistedUint8Array;
}

export const EphemeralKeyPairEncoding = {
  decode: (e: PersistedEphemeralKeyPair): EphemeralKeyPair | undefined => {
    const decodedData = decodeUint8Array(e.data);
    if (!decodedData) return undefined;
    return EphemeralKeyPair.fromBytes(decodedData);
  },
  encode: (e: EphemeralKeyPair) =>
    ({
      __type: "EphemeralKeyPair",
      data: encodeUint8Array(e.bcsToBytes()),
    }) as PersistedEphemeralKeyPair,
};

export const validateEphemeralKeyPair = (
  keyPair: EphemeralKeyPair,
): EphemeralKeyPair | undefined => (isValidEphemeralKeyPair(keyPair) ? keyPair : undefined);

export const isValidEphemeralKeyPair = (keyPair: EphemeralKeyPair): boolean => {
  if (keyPair.isExpired()) return false;
  return true;
};

/**
 * Create a new ephemeral key pair with a random private key and nonce.
 *
 * @param params Additional parameters for the ephemeral key pair
 */
export const createEphemeralKeyPair = ({
  expiryDateSecs = Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 7, // 7 days
  privateKey = Ed25519PrivateKey.generate(),
  ...options
}: Partial<ConstructorParameters<typeof EphemeralKeyPair>[0]> = {}) => {
  return new EphemeralKeyPair({ expiryDateSecs, privateKey, ...options });
};

export const createAndStoreEphemeralKeyPair = () => {
  const keyPair = createEphemeralKeyPair();
  const encoded = EphemeralKeyPairEncoding.encode(keyPair);
  window.localStorage.setItem(LOCAL_EPHEMERAL_KEY_PAIR_KEY, JSON.stringify(encoded));
  return keyPair;
};

export const getStoredEphemeralKeyPair = () => {
  try {
    const storedEphemeralKeyPair = window.localStorage.getItem(LOCAL_EPHEMERAL_KEY_PAIR_KEY);
    if (!storedEphemeralKeyPair) return undefined;
    const persistedEphemeralKeyPair = JSON.parse(
      storedEphemeralKeyPair,
    ) as PersistedEphemeralKeyPair;
    return EphemeralKeyPairEncoding.decode(persistedEphemeralKeyPair);
  } catch (error) {
    Logger.error(error, {
      message: "Failed to get stored ephemeral key pair",
    });
    return undefined;
  }
};
